// MIFT trilingual translations (EN default, PT, ES)
// Institutional name (verbatim): "MIFT — Integrated Mediation Methodology for Transnational Families"
// Subtitle: "Jurisdiction-Neutral Framework for Cross-Border Family Dispute Resolution"
export const translations = {
  en: {
    site: {
      name: "MIFT",
      full_name: "MIFT — Integrated Mediation Methodology for Transnational Families",
      tagline: "Jurisdiction-Neutral Framework for Cross-Border Family Dispute Resolution",
    },
    nav: {
      home: "Home", framework: "Framework", assessment: "Assessment", mediation: "Mediation Suitability",
      toolkit: "Toolkit", pilot: "Pilot Program", feedback: "Feedback", book: "Book",
      about: "About MIFT", developer: "Developer", research: "Research", disclaimer: "Disclaimer",
      join_network: "Join the Professional Network", admin: "Admin",
    },
    lang: { switch: "Language" },
    cta: {
      explore_framework: "Explore the MIFT Framework",
      use_assessment: "Use the Assessment Tool",
      download_toolkit: "Explore the Professional Toolkit",
      join_pilot: "Join the Pilot Program",
      provide_feedback: "Provide Professional Feedback",
      request_training: "Request a Training",
      collaborate: "Collaborate with MIFT",
      join_network: "Join the Professional Network",
    },
    home: {
      eyebrow: "A jurisdiction-neutral operational framework",
      hero_title: "A structured methodology for transnational family disputes.",
      hero_sub: "MIFT is a jurisdiction-neutral operational framework for attorneys, mediators, and other qualified professionals handling transnational family disputes. It supports cross-border screening, procedure design, professional coordination, and implementation planning across jurisdictions.",
      panel_title: "MIFT Implementation & Reach",
      panel_body: "MIFT is currently establishing its professional implementation and pilot infrastructure. Verified implementation metrics will be published as professionals begin using the framework, assessment tools, professional resources, and pilot program. Public metrics will reflect only actual, documented platform activity.",
      pillars_title: "What MIFT offers professionals",
      p1_title: "A structured methodology",
      p1_desc: "Four connected stages guide professionals from structured intake to child-centered safeguards, without prescribing legal outcomes.",
      p2_title: "Educational decision-support",
      p2_desc: "Assessments and toolkits designed as professional aids — never automated legal advice, never a substitute for qualified counsel.",
      p3_title: "Cross-border by design",
      p3_desc: "Built to help professionals navigate jurisdictional complexity, communication protocols, and enforceability considerations.",
      p4_title: "Privacy-conscious",
      p4_desc: "The platform collects only categorical, non-identifying data. No child names, no case numbers, no case narratives.",
      audience_title: "Built for U.S. and international practitioners",
      audience_body: "Designed for family-law attorneys, mediators, ADR professionals, bar-committee members, and academic programs working with internationally connected families.",
      screening_title: "Mediation is not appropriate in every case",
      screening_body: "MIFT includes a structured screening for safety, coercion or power imbalance, domestic violence indicators, urgency, and risk of wrongful removal or retention. MIFT does not itself determine the child's best interests.",
      screening_cta: "Open the Mediation Suitability & Safety Screening",
      implementation_title: "Implementation, compliance, recognition & enforceability",
      implementation_body: "A core MIFT component: planning how a cross-border decision or agreement will actually operate — including recognition considerations across jurisdictions, enforceability review, judicial approval pathways, foreign-counsel coordination, and logistical execution.",
      implementation_cta: "Use the Agreement Readiness Check",
      hague_urgency_title: "Urgent child-abduction or wrongful-retention matters",
      hague_urgency_body: "MIFT is not intended to delay urgent judicial, administrative, or Central Authority action. Users should promptly consult qualified counsel regarding applicable Hague Convention procedures and deadlines.",
      quick_start_title: "Get started",
      quick_start_sub: "Four ways to engage with MIFT as a practitioner.",
    },
    framework: {
      title: "The MIFT Framework",
      sub: "A four-stage operational methodology for transnational family dispute resolution.",
      s1_title: "Stage 1 — Structured Intake", s1_desc: "Capture the professional context of a cross-border family matter using categorical, non-identifying inputs.",
      s2_title: "Stage 2 — Contextual Mapping", s2_desc: "Map jurisdictional touchpoints, potential international instruments, and structural factors relevant to the family.",
      s3_title: "Stage 3 — Pathway Analysis", s3_desc: "Evaluate pathways including consensual mediation, coordinated legal action, and safeguarding referrals.",
      s4_title: "Stage 4 — Child-Centered Safeguards", s4_desc: "Anchor decisions in the child's routine, voice, safety, and continuity across jurisdictions.",
    },
    assessment: {
      title: "Interactive Cross-Border Case Assessment",
      sub: "Generates a MIFT Assessment Map — an educational, non-legal-advice professional decision-support output.",
      start: "Begin assessment",
      submit: "Generate MIFT Assessment Map",
      result_title: "Your MIFT Assessment Map",
      restart: "Start a new assessment",
      warning: "Do not enter identifying information about any real family or case. Use categorical answers only.",
      download_pdf: "Download Assessment Map (PDF)",
      pdf_note: "The PDF is professionally formatted and includes the MIFT disclaimer. No case identifiers are included.",
      q: {
        jurisdiction_relationship: "How would you categorize the jurisdictional touchpoints in this professional scenario?",
        hague_convention_context: "Is there a possible international child protection or abduction context?",
        child_habitual_residence_clarity: "Is the child's habitual residence clearly established?",
        parents_relationship_status: "What is the relationship status of the parents?",
        urgency_level: "How would you categorize the urgency?",
        communication_between_parents: "How would you describe communication between the parents?",
        prior_agreements_exist: "Are prior agreements between the parents in place?",
        representation_status: "Are both parties legally represented in each relevant jurisdiction?",
      },
      opts: {
        single_jurisdiction: "Primarily a single jurisdiction",
        multiple_jurisdictions: "Multiple jurisdictions involved",
        unclear: "Unclear at this stage",
        possible_hague_case: "Possibly relevant",
        not_relevant: "Not indicated",
        clear: "Clearly established",
        disputed: "Disputed",
        separated: "Separated / divorcing",
        never_married: "Never married",
        divorced: "Divorced",
        low: "Low", medium: "Medium", high: "High",
        functional: "Functional", strained: "Strained", hostile: "High-conflict / hostile",
        yes: "Yes", no: "No",
        represented: "Both represented", unrepresented: "One or both unrepresented",
      },
    },
    mediation: {
      title: "Mediation Suitability & Safety Assessment",
      sub: "A structured screening to help professionals evaluate whether mediation is potentially appropriate — never a determination of legal rights.",
      submit: "Generate suitability screening",
      restart: "Start a new screening",
      result_title: "Suitability Screening Result",
      q: {
        power_balance: "How would you categorize the power balance between parties?",
        safety_concerns_reported: "Have safety concerns been raised or reported?",
        both_parties_willing: "Are both parties voluntarily willing to explore mediation?",
        capacity_to_participate: "Do both parties appear to have capacity to participate meaningfully?",
        urgent_protective_measures_needed: "Are urgent protective or provisional measures needed?",
        prior_mediation_attempts: "Have prior mediation attempts occurred?",
        children_voice_considered: "Is there a framework to consider the child's voice?",
      },
      opts: {
        balanced: "Balanced", somewhat_imbalanced: "Somewhat imbalanced", significantly_imbalanced: "Significantly imbalanced",
        yes: "Yes", no: "No", unclear: "Unclear",
      },
    },
    toolkit: {
      title: "Professional Toolkit",
      sub: "MIFT framework resources — professional educational materials, not legal advice. English resources are available now (Version 1.0). Portuguese and Spanish versions will be enabled once approved.",
      download: "Download PDF",
      generating: "Preparing PDF...",
      in_preparation: "Professional resource in preparation",
      preparation_note: "This resource is being professionally reviewed and will be published once approved.",
    },
    pilot: {
      title: "Professional Pilot Program",
      sub: "Join a curated cohort of professionals implementing the MIFT methodology.",
      submit: "Submit pilot application",
      success: "Application received. A confirmation email has been sent through professional channels.",
      who_title: "Who can participate",
      who_body: "Attorneys, mediators, ADR professionals, family-law practitioners, bar-committee members, and academic researchers working with internationally connected families.",
      receive_title: "What you receive",
      receive_body: "Access to the MIFT Framework, the Cross-Border Case Assessment, the Mediation Suitability & Safety Screening, the Agreement Readiness Check, the Professional Toolkit (EN v1.0), and periodic implementation updates.",
      nopii_title: "No confidential client information is collected",
      nopii_body: "Do not submit children's names, dates of birth, passport numbers, case numbers, addresses, abuse narratives, or confidential documents. Only anonymized, professional feedback is used.",
      feedback_title: "How feedback is measured",
      feedback_body: "Feedback focuses on usability, clarity, repeat use, and implementation. No case-specific data is requested or retained.",
    },
    feedback: {
      title: "Professional Feedback",
      sub: "Structured professional feedback on the MIFT tools you have used.",
      submit: "Submit feedback",
      success: "Feedback received. Thank you.",
    },
    book: {
      title: "The Published Methodology",
      sub: "Verified bibliographic information for the Portuguese and English editions is shown below.",
      cover_caption: "First edition · 2026 · Portuguese",
      pt_edition: "Portuguese edition (verified)",
      en_edition: "English edition (verified)",
      pt_title_label: "Title",
      pt_title: "MIFT – Metodologia Integrada de Mediação em Famílias Transnacionais: Um Guia Operacional para Atuação Jurídica e Mediação em Disputas Familiares Transnacionais",
      authors_label: "Authors",
      authors: "Isabella Calamia Rinaldi and Diego Rycbczak Lopes",
      edition_label: "Edition", edition: "First edition",
      isbn: "ISBN", isbn_value: "978-65-979415-0-6",
      published: "Publication year", published_value: "2026",
      formats: "Formats", formats_value: "Print and Kindle editions",
      length: "Length", length_value: "217 pages",
      asin: "Amazon Kindle ASIN", asin_value: "B0GTJ19BD7",
      publisher_pending: "Publisher: forthcoming",
      date_pending: "Exact publication date: forthcoming",
      en_title_label: "Title",
      en_title: "M.I.F.T INTEGRATED MEDIATION METHODOLOGY FOR TRANSNATIONAL FAMILIES: An operational guide for legal practice and mediation in transnational family disputes",
      en_authors: "Isabella Calamia Rinaldi and Diego Rycbczak Lopes",
      en_edition_value: "First edition (Kindle)",
      en_language_label: "Language", en_language_value: "English",
      en_pubdate_label: "Publication date", en_pubdate_value: "September 25, 2026",
      en_formats_value: "Kindle Edition (eBook)",
      en_length_value: "225 pages",
      en_asin_value: "B0HKZZPP1Z",
      overview: "Overview",
      overview_body: "The MIFT publication is designed as an operational framework for transnational family disputes and includes structured professional tools and implementation-oriented resources.",
      purchase_amazon_br: "Amazon Brazil — Kindle (Portuguese edition)",
      purchase_amazon_us: "Amazon USA — Kindle (English edition)",
    },
    about: {
      title: "About MIFT",
      sub: "A child-centered, cross-border operational methodology.",
      body1: "MIFT is a professional operational methodology developed to support practitioners working with transnational family disputes. It is deliberately educational and non-adversarial — a decision-support architecture, not a legal-services product.",
      body2: "MIFT does not determine legal rights, does not provide legal advice, and does not replace consultation with qualified counsel in each relevant jurisdiction.",
      principles_title: "Core principles",
      pr1: "Child-centered", pr2: "Cross-border by design", pr3: "Educational, not adversarial", pr4: "Privacy-conscious",
    },
    developer: {
      title: "About the Developer",
      name: "Isabella Calamia Rinaldi",
      role: "Brazilian attorney focused on International Family Law, international family mediation and cross-border family dispute resolution. Developer of the MIFT methodology.",
      credentials_title: "Professional background",
      c1: "Brazilian attorney; Family Law practice since 2017.",
      c2: "Professional focus on International Family Law and transnational family disputes.",
      c3: "Postgraduate education in International Law.",
      c4: "Postgraduate education in Family Law.",
      c5: "LL.M. candidate at the University of Dayton.",
      c6: "Experience involving international and cross-border family-law matters.",
      c7: "Author/co-author and developer of the MIFT methodology.",
      c8: "Professional publications, trainings, presentations, and research in the field (specific verified details to be added).",
      licensure_notice: "Licensed to practice law in Brazil. Not admitted to practice law in the United States.",
      photo_placeholder: "Professional photograph forthcoming",
    },
    research: {
      title: "Research & Institutional Resources",
      sub: "A curated library of publicly available institutional sources relevant to transnational family dispute resolution.",
      visit: "Visit resource",
      references_not_endorsements: "These are educational references from independent institutions. Their inclusion is not an endorsement of MIFT by those bodies, and MIFT does not represent, and is not affiliated with, any of them.",
      categories: {
        institutions: "International Institutions & Central Authorities",
        academic: "Academic & Research",
        professional: "Professional Associations & Mediation Bodies",
      },
    },
    disclaimer: {
      title: "Professional Disclaimer",
      body: "The MIFT platform is a professional educational framework and decision-support architecture. It is not a legal-services product. It does not determine legal rights, does not constitute legal advice, does not create an attorney-client relationship, and does not replace consultation with qualified counsel in each relevant jurisdiction. The platform does not accept case-specific inputs and intentionally does not collect children's names, dates of birth, addresses, case numbers, passport information, individual immigration status, detailed abuse allegations, court documents, or other identifiable real-case data. No fees, no case-results claims, no legal-services marketing, no automated legal-advice chatbots.",
      pre_tool: "MIFT is an educational and professional decision-support framework. It does not provide legal advice, create an attorney-client relationship, determine jurisdiction, or replace the judgment of appropriately licensed professionals.",
      hague_urgency: "MIFT is not intended to delay urgent judicial, administrative, or Central Authority action. Users should promptly consult qualified counsel regarding applicable Hague Convention procedures and deadlines.",
    },
    forms: {
      full_name: "Full name", email: "Email", professional_field: "Professional field", country: "Country",
      organization: "Organization (optional)", years_experience: "Years of experience",
      interested_pilot: "Interested in the pilot program",
      interested_training: "Interested in a training",
      interested_collab: "Interested in collaboration",
      consent: "I understand this is an educational professional framework, not legal advice.",
      required: "Required",
      caseload_size: "Approximate professional caseload involving cross-border matters",
      reason_to_join: "Why would you like to join the MIFT pilot?",
      tool_used: "Which MIFT tool did you use?",
      usefulness: "Usefulness (1–5)", clarity: "Clarity (1–5)", would_recommend: "Would you recommend it to a colleague?",
      comments: "Comments (non-identifying only)",
      training_type: "Type of training requested",
      collaboration_type: "Type of collaboration",
      description: "Description (do not include case-identifying details)",
    },
    footer: {
      copyright: "© MIFT — Integrated Mediation Methodology for Transnational Families",
      pre_launch: "Pre-launch / MVP build. Public launch date to be confirmed.",
      dev_log: "Development Log (admin)",
      full_disclaimer: "MIFT is an educational and professional decision-support framework. It does not provide legal advice, create an attorney-client relationship, determine jurisdiction, or replace the judgment of appropriately licensed professionals. Institutional references listed on the Research page are educational sources, not endorsements.",
    },
    admin: {
      title: "MIFT Admin",
      login_title: "Admin sign in",
      email: "Email", password: "Password", sign_in: "Sign in", sign_out: "Sign out",
      unauthorized: "Invalid credentials",
      env_label: "Environment", env_production: "Production", env_test: "Test", env_all: "All",
      start_date: "Start date", end_date: "End date", apply: "Apply", clear: "Clear",
      export_csv: "Export CSV",
      totals: "Aggregated totals",
      by_country: "By country", by_field: "By field", by_language: "By language",
      dev_log_title: "Development Log (real milestones only)",
      dev_log_add: "Add milestone",
      dev_log_title_field: "Title", dev_log_version: "Version (optional)",
      dev_log_notes: "Notes", dev_log_occurred: "Actual occurrence date (ISO)",
      dev_log_save: "Save milestone",
      no_data: "No production activity recorded yet.",
    },
  },
  pt: {
    site: {
      name: "MIFT",
      full_name: "MIFT — Metodologia Integrada de Mediação para Famílias Transnacionais",
      tagline: "Framework Neutro em Relação à Jurisdição para Resolução de Disputas Familiares Transfronteiriças",
    },
    nav: {
      home: "Início", framework: "Framework", assessment: "Avaliação", mediation: "Adequação à Mediação",
      toolkit: "Toolkit", pilot: "Programa Piloto", feedback: "Feedback", book: "Livro",
      about: "Sobre o MIFT", developer: "Desenvolvedora", research: "Pesquisa", disclaimer: "Aviso",
      join_network: "Junte-se à Rede Profissional", admin: "Admin",
    },
    lang: { switch: "Idioma" },
    cta: {
      explore_framework: "Explorar o Framework MIFT",
      use_assessment: "Usar a Ferramenta de Avaliação",
      download_toolkit: "Explorar o Toolkit Profissional",
      join_pilot: "Participar do Programa Piloto",
      provide_feedback: "Enviar Feedback Profissional",
      request_training: "Solicitar uma Formação",
      collaborate: "Colaborar com o MIFT",
      join_network: "Junte-se à Rede Profissional",
    },
    home: {
      eyebrow: "Um framework operacional neutro em relação à jurisdição",
      hero_title: "Uma metodologia estruturada para disputas familiares transnacionais.",
      hero_sub: "O MIFT é um framework operacional neutro em relação à jurisdição, dirigido a advogados, mediadores e outros profissionais qualificados que atuam em disputas familiares transnacionais. Apoia a triagem transfronteiriça, o desenho de procedimento, a coordenação profissional e o planejamento de implementação entre jurisdições.",
      panel_title: "Implementação e Alcance do MIFT",
      panel_body: "O MIFT está atualmente estabelecendo sua infraestrutura de implementação profissional e do programa piloto. Métricas de implementação verificadas serão publicadas à medida que profissionais utilizem o framework, as ferramentas de avaliação, os recursos profissionais e o programa piloto. As métricas públicas refletirão apenas a atividade real e documentada da plataforma.",
      pillars_title: "O que o MIFT oferece aos profissionais",
      p1_title: "Uma metodologia estruturada",
      p1_desc: "Quatro estágios conectados guiam profissionais da admissão estruturada às salvaguardas centradas na criança, sem prescrever resultados legais.",
      p2_title: "Apoio educacional à decisão",
      p2_desc: "Avaliações e toolkits desenhados como auxílios profissionais — nunca aconselhamento jurídico automatizado, nunca substituto de advogado qualificado.",
      p3_title: "Transfronteiriço por concepção",
      p3_desc: "Feito para ajudar profissionais a navegar complexidade jurisdicional, protocolos de comunicação e questões de exequibilidade.",
      p4_title: "Consciente da privacidade",
      p4_desc: "A plataforma coleta apenas dados categóricos e não identificáveis. Sem nomes de crianças, sem números de caso, sem narrativas.",
      audience_title: "Concebido para profissionais nos EUA e internacionalmente",
      audience_body: "Desenhado para advogados de família, mediadores, profissionais de ADR, membros de comitês de ordens de advogados e programas acadêmicos que trabalham com famílias conectadas internacionalmente.",
      screening_title: "A mediação não é apropriada em todos os casos",
      screening_body: "O MIFT inclui uma triagem estruturada para segurança, coerção ou desequilíbrio de poder, indicadores de violência doméstica, urgência e risco de subtração ou retenção indevida. O MIFT não determina, por si só, o melhor interesse da criança.",
      screening_cta: "Abrir a Triagem de Adequação à Mediação e Segurança",
      implementation_title: "Implementação, conformidade, reconhecimento e exequibilidade",
      implementation_body: "Um componente central do MIFT: planejar como uma decisão ou acordo transfronteiriço funcionará na prática — incluindo considerações de reconhecimento entre jurisdições, revisão de exequibilidade, caminhos de homologação judicial, coordenação com advogados estrangeiros e execução logística.",
      implementation_cta: "Usar o Check de Prontidão do Acordo",
      hague_urgency_title: "Casos urgentes de subtração ou retenção indevida de crianças",
      hague_urgency_body: "O MIFT não pretende atrasar ações judiciais, administrativas ou de Autoridade Central urgentes. Usuários devem consultar prontamente um advogado qualificado sobre os procedimentos e prazos aplicáveis da Convenção de Haia.",
      quick_start_title: "Comece agora",
      quick_start_sub: "Quatro formas de engajar com o MIFT como profissional.",
    },
    framework: {
      title: "O Framework MIFT",
      sub: "Uma metodologia operacional de quatro estágios para resolução de disputas familiares transnacionais.",
      s1_title: "Estágio 1 — Admissão Estruturada", s1_desc: "Capture o contexto profissional de um caso familiar transfronteiriço utilizando entradas categóricas e não identificáveis.",
      s2_title: "Estágio 2 — Mapeamento Contextual", s2_desc: "Mapeie pontos de contato jurisdicionais, possíveis instrumentos internacionais e fatores estruturais.",
      s3_title: "Estágio 3 — Análise de Caminhos", s3_desc: "Avalie caminhos, incluindo mediação consensual, ação legal coordenada e encaminhamentos de proteção.",
      s4_title: "Estágio 4 — Salvaguardas Centradas na Criança", s4_desc: "Ancore decisões na rotina, voz, segurança e continuidade da criança entre jurisdições.",
    },
    assessment: {
      title: "Avaliação Interativa de Caso Transfronteiriço",
      sub: "Gera um Mapa de Avaliação MIFT — um resultado educacional de apoio à decisão profissional, sem aconselhamento jurídico.",
      start: "Iniciar avaliação", submit: "Gerar Mapa de Avaliação MIFT",
      result_title: "Seu Mapa de Avaliação MIFT", restart: "Iniciar nova avaliação",
      warning: "Não insira informações identificáveis sobre nenhuma família ou caso real. Utilize apenas respostas categóricas.",
      download_pdf: "Baixar Mapa de Avaliação (PDF)",
      pdf_note: "O PDF é formatado profissionalmente e inclui o aviso MIFT. Nenhum identificador de caso é incluído.",
      q: {
        jurisdiction_relationship: "Como você categorizaria os pontos de contato jurisdicionais neste cenário profissional?",
        hague_convention_context: "Existe um possível contexto de proteção internacional da criança ou de subtração?",
        child_habitual_residence_clarity: "A residência habitual da criança está claramente estabelecida?",
        parents_relationship_status: "Qual é o estado da relação dos pais?",
        urgency_level: "Como você categorizaria a urgência?",
        communication_between_parents: "Como você descreveria a comunicação entre os pais?",
        prior_agreements_exist: "Existem acordos anteriores entre os pais?",
        representation_status: "Ambas as partes têm representação legal em cada jurisdição relevante?",
      },
      opts: {
        single_jurisdiction: "Principalmente uma jurisdição", multiple_jurisdictions: "Múltiplas jurisdições envolvidas",
        unclear: "Incerto neste estágio", possible_hague_case: "Possivelmente relevante", not_relevant: "Não indicado",
        clear: "Claramente estabelecida", disputed: "Disputada",
        separated: "Separados / em divórcio", never_married: "Nunca casados", divorced: "Divorciados",
        low: "Baixa", medium: "Média", high: "Alta",
        functional: "Funcional", strained: "Tensa", hostile: "Alto conflito / hostil",
        yes: "Sim", no: "Não",
        represented: "Ambos representados", unrepresented: "Um ou ambos sem representação",
      },
    },
    mediation: {
      title: "Avaliação de Adequação à Mediação e Segurança",
      sub: "Uma triagem estruturada para ajudar profissionais a avaliar se a mediação é potencialmente apropriada — nunca uma determinação de direitos.",
      submit: "Gerar triagem de adequação", restart: "Iniciar nova triagem",
      result_title: "Resultado da Triagem de Adequação",
      q: {
        power_balance: "Como categorizaria o equilíbrio de poder entre as partes?",
        safety_concerns_reported: "Foram levantadas ou relatadas preocupações de segurança?",
        both_parties_willing: "Ambas as partes desejam voluntariamente explorar mediação?",
        capacity_to_participate: "Ambas as partes parecem ter capacidade para participar significativamente?",
        urgent_protective_measures_needed: "São necessárias medidas urgentes de proteção ou provisórias?",
        prior_mediation_attempts: "Houve tentativas anteriores de mediação?",
        children_voice_considered: "Existe estrutura para considerar a voz da criança?",
      },
      opts: {
        balanced: "Equilibrado", somewhat_imbalanced: "Algo desequilibrado", significantly_imbalanced: "Significativamente desequilibrado",
        yes: "Sim", no: "Não", unclear: "Incerto",
      },
    },
    toolkit: {
      title: "Toolkit Profissional",
      sub: "Recursos do framework MIFT — materiais educacionais profissionais, não aconselhamento jurídico. Os recursos em inglês estão disponíveis agora (Versão 1.0). As versões em português e espanhol serão liberadas após aprovação.",
      download: "Baixar PDF",
      generating: "Preparando PDF...",
      in_preparation: "Recurso profissional em preparação",
      preparation_note: "Este recurso está em revisão profissional e será publicado após aprovação.",
    },
    pilot: {
      title: "Programa Piloto Profissional",
      sub: "Junte-se a um grupo curado de profissionais implementando a metodologia MIFT.",
      submit: "Enviar candidatura",
      success: "Candidatura recebida. Um e-mail de confirmação foi enviado por canais profissionais.",
      who_title: "Quem pode participar",
      who_body: "Advogados, mediadores, profissionais de ADR, praticantes de Direito de Família, membros de comitês de ordens de advogados e pesquisadores acadêmicos que trabalham com famílias conectadas internacionalmente.",
      receive_title: "O que você recebe",
      receive_body: "Acesso ao Framework MIFT, à Avaliação de Caso Transfronteiriço, à Triagem de Adequação à Mediação e Segurança, ao Check de Prontidão do Acordo, ao Toolkit Profissional (EN v1.0) e a atualizações periódicas de implementação.",
      nopii_title: "Nenhuma informação confidencial de cliente é coletada",
      nopii_body: "Não envie nomes de crianças, datas de nascimento, números de passaporte, números de processo, endereços, narrativas de abuso ou documentos confidenciais. Apenas feedback profissional anonimizado é utilizado.",
      feedback_title: "Como o feedback é medido",
      feedback_body: "O feedback foca em usabilidade, clareza, uso recorrente e implementação. Nenhum dado específico de caso é solicitado ou retido.",
    },
    feedback: { title: "Feedback Profissional", sub: "Feedback profissional estruturado sobre as ferramentas MIFT utilizadas.", submit: "Enviar feedback", success: "Feedback recebido. Obrigado." },
    book: {
      title: "A Metodologia Publicada",
      sub: "As informações bibliográficas verificadas das edições em português e inglês são exibidas abaixo.",
      cover_caption: "Primeira edição · 2026 · Português",
      pt_edition: "Edição em português (verificada)",
      en_edition: "Edição em inglês (verificada)",
      pt_title_label: "Título",
      pt_title: "MIFT – Metodologia Integrada de Mediação em Famílias Transnacionais: Um Guia Operacional para Atuação Jurídica e Mediação em Disputas Familiares Transnacionais",
      authors_label: "Autores",
      authors: "Isabella Calamia Rinaldi e Diego Rycbczak Lopes",
      edition_label: "Edição", edition: "Primeira edição",
      isbn: "ISBN", isbn_value: "978-65-979415-0-6",
      published: "Ano de publicação", published_value: "2026",
      formats: "Formatos", formats_value: "Edições impressa e Kindle",
      length: "Extensão", length_value: "217 páginas",
      asin: "ASIN Amazon Kindle", asin_value: "B0GTJ19BD7",
      publisher_pending: "Editora: em breve",
      date_pending: "Data exata de publicação: em breve",
      en_title_label: "Título",
      en_title: "M.I.F.T INTEGRATED MEDIATION METHODOLOGY FOR TRANSNATIONAL FAMILIES: An operational guide for legal practice and mediation in transnational family disputes",
      en_authors: "Isabella Calamia Rinaldi e Diego Rycbczak Lopes",
      en_edition_value: "Primeira edição (Kindle)",
      en_language_label: "Idioma", en_language_value: "Inglês",
      en_pubdate_label: "Data de publicação", en_pubdate_value: "25 de setembro de 2026",
      en_formats_value: "Edição Kindle (e-book)",
      en_length_value: "225 páginas",
      en_asin_value: "B0HKZZPP1Z",
      overview: "Visão geral",
      overview_body: "A publicação MIFT é concebida como um framework operacional para disputas familiares transnacionais e inclui ferramentas profissionais estruturadas e recursos orientados à implementação.",
      purchase_amazon_br: "Amazon Brasil — Kindle (edição em português)",
      purchase_amazon_us: "Amazon EUA — Kindle (edição em inglês)",
    },
    about: {
      title: "Sobre o MIFT", sub: "Uma metodologia operacional transfronteiriça e centrada na criança.",
      body1: "O MIFT é uma metodologia operacional profissional desenvolvida para apoiar profissionais que atuam em disputas familiares transnacionais. É deliberadamente educacional e não adversarial — uma arquitetura de apoio à decisão, não um produto de serviços jurídicos.",
      body2: "O MIFT não determina direitos legais, não fornece aconselhamento jurídico e não substitui a consulta a advogados qualificados em cada jurisdição relevante.",
      principles_title: "Princípios centrais",
      pr1: "Centrado na criança", pr2: "Transfronteiriço por concepção", pr3: "Educacional, não adversarial", pr4: "Consciente da privacidade",
    },
    developer: {
      title: "Sobre a Desenvolvedora", name: "Isabella Calamia Rinaldi",
      role: "Advogada brasileira com foco em Direito Internacional de Família, mediação familiar internacional e resolução de disputas familiares transfronteiriças. Desenvolvedora da metodologia MIFT.",
      credentials_title: "Trajetória profissional",
      c1: "Advogada brasileira; prática em Direito de Família desde 2017.",
      c2: "Foco profissional em Direito Internacional de Família e disputas familiares transnacionais.",
      c3: "Pós-graduação em Direito Internacional.",
      c4: "Pós-graduação em Direito de Família.",
      c5: "Candidata a LL.M. na University of Dayton.",
      c6: "Experiência em matéria familiar internacional e transfronteiriça.",
      c7: "Autora/coautora e desenvolvedora da metodologia MIFT.",
      c8: "Publicações profissionais, formações, apresentações e pesquisa na área (detalhes verificados a serem adicionados).",
      licensure_notice: "Licenciada para exercer o direito no Brasil. Não admitida a exercer o direito nos Estados Unidos.",
      photo_placeholder: "Fotografia profissional em breve",
    },
    research: {
      title: "Pesquisa e Recursos Institucionais",
      sub: "Uma biblioteca curada de fontes institucionais publicamente disponíveis relevantes para a resolução de disputas familiares transnacionais.",
      visit: "Acessar recurso",
      references_not_endorsements: "Estas são referências educacionais de instituições independentes. Sua inclusão não constitui endosso do MIFT por esses órgãos, e o MIFT não representa nenhum deles nem tem qualquer afiliação com eles.",
      categories: { institutions: "Instituições Internacionais e Autoridades Centrais", academic: "Acadêmico e Pesquisa", professional: "Associações Profissionais e Órgãos de Mediação" },
    },
    disclaimer: {
      title: "Aviso Profissional",
      body: "A plataforma MIFT é um framework educacional profissional e uma arquitetura de apoio à decisão. Não é um produto de serviços jurídicos. Não determina direitos legais, não constitui aconselhamento jurídico, não cria relação advogado-cliente e não substitui a consulta com advogados qualificados em cada jurisdição relevante. A plataforma não aceita informações específicas de caso e intencionalmente não coleta nomes, datas de nascimento, endereços, números de processo, informações de passaporte, status migratório individual, alegações detalhadas de abuso, documentos judiciais ou outros dados identificáveis. Sem taxas, sem promessas de resultado, sem marketing de serviços jurídicos, sem chatbot jurídico automatizado.",
      pre_tool: "O MIFT é um framework educacional e profissional de apoio à decisão. Não fornece aconselhamento jurídico, não cria relação advogado-cliente, não determina jurisdição e não substitui o julgamento de profissionais devidamente licenciados.",
      hague_urgency: "O MIFT não pretende atrasar ações judiciais, administrativas ou de Autoridade Central urgentes. Usuários devem consultar prontamente um advogado qualificado sobre os procedimentos e prazos aplicáveis da Convenção de Haia.",
    },
    forms: {
      full_name: "Nome completo", email: "E-mail", professional_field: "Área profissional", country: "País",
      organization: "Organização (opcional)", years_experience: "Anos de experiência",
      interested_pilot: "Interesse no programa piloto",
      interested_training: "Interesse em formação",
      interested_collab: "Interesse em colaboração",
      consent: "Compreendo que este é um framework educacional profissional, não aconselhamento jurídico.",
      required: "Obrigatório",
      caseload_size: "Volume aproximado de casos transfronteiriços",
      reason_to_join: "Por que gostaria de participar do piloto MIFT?",
      tool_used: "Qual ferramenta MIFT você utilizou?",
      usefulness: "Utilidade (1–5)", clarity: "Clareza (1–5)", would_recommend: "Recomendaria a um colega?",
      comments: "Comentários (apenas não identificáveis)",
      training_type: "Tipo de formação solicitada",
      collaboration_type: "Tipo de colaboração",
      description: "Descrição (não inclua detalhes identificáveis)",
    },
    footer: {
      copyright: "© MIFT — Metodologia Integrada de Mediação para Famílias Transnacionais",
      pre_launch: "Pré-lançamento / build MVP. Data de lançamento público a ser confirmada.",
      dev_log: "Log de Desenvolvimento (admin)",
      full_disclaimer: "O MIFT é um framework educacional e profissional de apoio à decisão. Não fornece aconselhamento jurídico, não cria relação advogado-cliente, não determina jurisdição e não substitui o julgamento de profissionais devidamente licenciados. As referências institucionais listadas na página Pesquisa são fontes educacionais, não endossos.",
    },
    admin: {
      title: "MIFT Admin",
      login_title: "Acesso administrativo",
      email: "E-mail", password: "Senha", sign_in: "Entrar", sign_out: "Sair",
      unauthorized: "Credenciais inválidas",
      env_label: "Ambiente", env_production: "Produção", env_test: "Teste", env_all: "Tudo",
      start_date: "Data inicial", end_date: "Data final", apply: "Aplicar", clear: "Limpar",
      export_csv: "Exportar CSV",
      totals: "Totais agregados",
      by_country: "Por país", by_field: "Por área", by_language: "Por idioma",
      dev_log_title: "Log de Desenvolvimento (apenas marcos reais)",
      dev_log_add: "Adicionar marco",
      dev_log_title_field: "Título", dev_log_version: "Versão (opcional)",
      dev_log_notes: "Notas", dev_log_occurred: "Data real de ocorrência (ISO)",
      dev_log_save: "Salvar marco",
      no_data: "Nenhuma atividade de produção registrada ainda.",
    },
  },
  es: {
    site: {
      name: "MIFT",
      full_name: "MIFT — Metodología Integrada de Mediación para Familias Transnacionales",
      tagline: "Marco Neutral en Materia de Jurisdicción para la Resolución de Disputas Familiares Transfronterizas",
    },
    nav: {
      home: "Inicio", framework: "Marco", assessment: "Evaluación", mediation: "Adecuación a la Mediación",
      toolkit: "Kit", pilot: "Programa Piloto", feedback: "Retroalimentación", book: "Libro",
      about: "Sobre MIFT", developer: "Desarrolladora", research: "Investigación", disclaimer: "Aviso",
      join_network: "Únete a la Red Profesional", admin: "Admin",
    },
    lang: { switch: "Idioma" },
    cta: {
      explore_framework: "Explorar el Marco MIFT",
      use_assessment: "Usar la Herramienta de Evaluación",
      download_toolkit: "Explorar el Kit Profesional",
      join_pilot: "Participar en el Programa Piloto",
      provide_feedback: "Enviar Retroalimentación",
      request_training: "Solicitar una Formación",
      collaborate: "Colaborar con MIFT",
      join_network: "Únete a la Red Profesional",
    },
    home: {
      eyebrow: "Un marco operativo neutral en materia de jurisdicción",
      hero_title: "Una metodología estructurada para disputas familiares transnacionales.",
      hero_sub: "MIFT es un marco operativo neutral en materia de jurisdicción para abogados, mediadores y otros profesionales calificados que trabajan con disputas familiares transnacionales. Apoya la evaluación transfronteriza, el diseño de procedimiento, la coordinación profesional y la planificación de implementación entre jurisdicciones.",
      panel_title: "Implementación y Alcance de MIFT",
      panel_body: "MIFT está estableciendo actualmente su infraestructura de implementación profesional y el programa piloto. Las métricas de implementación verificadas se publicarán a medida que los profesionales utilicen el marco, las herramientas de evaluación, los recursos profesionales y el programa piloto. Las métricas públicas reflejarán únicamente la actividad real y documentada de la plataforma.",
      pillars_title: "Lo que MIFT ofrece a los profesionales",
      p1_title: "Una metodología estructurada",
      p1_desc: "Cuatro etapas conectadas guían a los profesionales desde el ingreso estructurado hasta las salvaguardas centradas en la niñez, sin prescribir resultados legales.",
      p2_title: "Apoyo educativo a la decisión",
      p2_desc: "Evaluaciones y kits diseñados como auxilios profesionales — nunca asesoría legal automatizada, nunca sustituto de asesoría calificada.",
      p3_title: "Transfronterizo por diseño",
      p3_desc: "Creado para ayudar a los profesionales a navegar la complejidad jurisdiccional, protocolos de comunicación y aspectos de ejecución.",
      p4_title: "Consciente de la privacidad",
      p4_desc: "La plataforma solo recoge datos categóricos y no identificables. Sin nombres de niños, sin números de caso, sin narrativas.",
      audience_title: "Diseñado para profesionales en EE. UU. e internacionalmente",
      audience_body: "Pensado para abogados de familia, mediadores, profesionales de ADR, integrantes de comités de colegios de abogados y programas académicos que trabajan con familias conectadas internacionalmente.",
      screening_title: "La mediación no es apropiada en todos los casos",
      screening_body: "MIFT incluye una evaluación estructurada de seguridad, coerción o desequilibrio de poder, indicadores de violencia doméstica, urgencia y riesgo de sustracción o retención indebida. MIFT no determina, por sí solo, el interés superior de la niñez.",
      screening_cta: "Abrir la Evaluación de Adecuación a la Mediación y Seguridad",
      implementation_title: "Implementación, cumplimiento, reconocimiento y ejecutabilidad",
      implementation_body: "Un componente central de MIFT: planificar cómo una decisión o acuerdo transfronterizo funcionará en la práctica — incluyendo consideraciones de reconocimiento entre jurisdicciones, revisión de ejecutabilidad, vías de homologación judicial, coordinación con abogados extranjeros y ejecución logística.",
      implementation_cta: "Usar el Chequeo de Preparación del Acuerdo",
      hague_urgency_title: "Casos urgentes de sustracción o retención indebida de menores",
      hague_urgency_body: "MIFT no busca demorar acciones judiciales, administrativas o de Autoridad Central urgentes. Los usuarios deben consultar prontamente a un abogado calificado sobre los procedimientos y plazos aplicables del Convenio de La Haya.",
      quick_start_title: "Comenzar",
      quick_start_sub: "Cuatro formas de involucrarte con MIFT como profesional.",
    },
    framework: {
      title: "El Marco MIFT",
      sub: "Una metodología operativa de cuatro etapas para la resolución de disputas familiares transnacionales.",
      s1_title: "Etapa 1 — Ingreso Estructurado", s1_desc: "Capture el contexto profesional de un asunto familiar transfronterizo utilizando entradas categóricas y no identificables.",
      s2_title: "Etapa 2 — Mapeo Contextual", s2_desc: "Mapee los puntos de contacto jurisdiccionales, posibles instrumentos internacionales y factores estructurales.",
      s3_title: "Etapa 3 — Análisis de Vías", s3_desc: "Evalúe vías incluyendo mediación consensuada, acción legal coordinada y derivaciones de protección.",
      s4_title: "Etapa 4 — Salvaguardas Centradas en la Niñez", s4_desc: "Ancle las decisiones en la rutina, voz, seguridad y continuidad del menor entre jurisdicciones.",
    },
    assessment: {
      title: "Evaluación Interactiva de Caso Transfronterizo",
      sub: "Genera un Mapa de Evaluación MIFT — un resultado educativo de apoyo a la decisión profesional, sin asesoría legal.",
      start: "Iniciar evaluación", submit: "Generar Mapa de Evaluación MIFT",
      result_title: "Su Mapa de Evaluación MIFT", restart: "Iniciar nueva evaluación",
      warning: "No ingrese información identificable de ninguna familia o caso real. Use solo respuestas categóricas.",
      download_pdf: "Descargar Mapa de Evaluación (PDF)",
      pdf_note: "El PDF está formateado profesionalmente e incluye el aviso MIFT. No incluye identificadores del caso.",
      q: {
        jurisdiction_relationship: "¿Cómo categorizaría los puntos de contacto jurisdiccionales en este escenario profesional?",
        hague_convention_context: "¿Existe un posible contexto de protección internacional de la niñez o sustracción?",
        child_habitual_residence_clarity: "¿Está claramente establecida la residencia habitual del menor?",
        parents_relationship_status: "¿Cuál es el estado de la relación de los progenitores?",
        urgency_level: "¿Cómo categorizaría la urgencia?",
        communication_between_parents: "¿Cómo describiría la comunicación entre los progenitores?",
        prior_agreements_exist: "¿Existen acuerdos previos entre los progenitores?",
        representation_status: "¿Ambas partes tienen representación legal en cada jurisdicción relevante?",
      },
      opts: {
        single_jurisdiction: "Principalmente una jurisdicción", multiple_jurisdictions: "Múltiples jurisdicciones involucradas",
        unclear: "Poco claro en esta etapa", possible_hague_case: "Posiblemente relevante", not_relevant: "No indicado",
        clear: "Claramente establecida", disputed: "Disputada",
        separated: "Separados / en divorcio", never_married: "Nunca casados", divorced: "Divorciados",
        low: "Baja", medium: "Media", high: "Alta",
        functional: "Funcional", strained: "Tensa", hostile: "Alto conflicto / hostil",
        yes: "Sí", no: "No",
        represented: "Ambos representados", unrepresented: "Uno o ambos sin representación",
      },
    },
    mediation: {
      title: "Evaluación de Adecuación a la Mediación y Seguridad",
      sub: "Un cribado estructurado para ayudar a los profesionales a evaluar si la mediación es potencialmente apropiada — nunca una determinación de derechos.",
      submit: "Generar cribado de adecuación", restart: "Iniciar nuevo cribado",
      result_title: "Resultado del Cribado de Adecuación",
      q: {
        power_balance: "¿Cómo categorizaría el equilibrio de poder entre las partes?",
        safety_concerns_reported: "¿Se han planteado preocupaciones de seguridad?",
        both_parties_willing: "¿Ambas partes desean voluntariamente explorar la mediación?",
        capacity_to_participate: "¿Ambas partes parecen tener capacidad para participar de manera significativa?",
        urgent_protective_measures_needed: "¿Se necesitan medidas urgentes de protección o provisionales?",
        prior_mediation_attempts: "¿Ha habido intentos previos de mediación?",
        children_voice_considered: "¿Existe un marco para considerar la voz del menor?",
      },
      opts: {
        balanced: "Equilibrado", somewhat_imbalanced: "Algo desequilibrado", significantly_imbalanced: "Significativamente desequilibrado",
        yes: "Sí", no: "No", unclear: "Poco claro",
      },
    },
    toolkit: {
      title: "Kit Profesional",
      sub: "Recursos del marco MIFT — materiales educativos profesionales, no asesoría legal. Los recursos en inglés están disponibles ahora (Versión 1.0). Las versiones en portugués y español se habilitarán una vez aprobadas.",
      download: "Descargar PDF",
      generating: "Preparando PDF...",
      in_preparation: "Recurso profesional en preparación",
      preparation_note: "Este recurso está en revisión profesional y se publicará una vez aprobado.",
    },
    pilot: {
      title: "Programa Piloto Profesional",
      sub: "Únete a una cohorte curada de profesionales que implementan la metodología MIFT.",
      submit: "Enviar solicitud",
      success: "Solicitud recibida. Se ha enviado un correo de confirmación por canales profesionales.",
      who_title: "Quiénes pueden participar",
      who_body: "Abogados, mediadores, profesionales de ADR, practicantes de derecho de familia, integrantes de comités de colegios de abogados y investigadores académicos que trabajan con familias conectadas internacionalmente.",
      receive_title: "Qué recibes",
      receive_body: "Acceso al Marco MIFT, la Evaluación de Caso Transfronterizo, la Evaluación de Adecuación a la Mediación y Seguridad, el Chequeo de Preparación del Acuerdo, el Kit Profesional (EN v1.0) y actualizaciones periódicas de implementación.",
      nopii_title: "No se recopila información confidencial de clientes",
      nopii_body: "No envíes nombres de niños, fechas de nacimiento, números de pasaporte, números de expediente, domicilios, relatos de abuso ni documentos confidenciales. Solo se utiliza retroalimentación profesional anonimizada.",
      feedback_title: "Cómo se mide la retroalimentación",
      feedback_body: "La retroalimentación se enfoca en usabilidad, claridad, uso recurrente e implementación. No se solicita ni se conserva información específica del caso.",
    },
    feedback: { title: "Retroalimentación Profesional", sub: "Retroalimentación profesional estructurada sobre las herramientas MIFT utilizadas.", submit: "Enviar retroalimentación", success: "Retroalimentación recibida. Gracias." },
    book: {
      title: "La Metodología Publicada",
      sub: "A continuación se muestra la información bibliográfica verificada de las ediciones en portugués e inglés.",
      cover_caption: "Primera edición · 2026 · Portugués",
      pt_edition: "Edición en portugués (verificada)",
      en_edition: "Edición en inglés (verificada)",
      pt_title_label: "Título",
      pt_title: "MIFT – Metodologia Integrada de Mediação em Famílias Transnacionais: Um Guia Operacional para Atuação Jurídica e Mediação em Disputas Familiares Transnacionais",
      authors_label: "Autores",
      authors: "Isabella Calamia Rinaldi y Diego Rycbczak Lopes",
      edition_label: "Edición", edition: "Primera edición",
      isbn: "ISBN", isbn_value: "978-65-979415-0-6",
      published: "Año de publicación", published_value: "2026",
      formats: "Formatos", formats_value: "Ediciones impresa y Kindle",
      length: "Extensión", length_value: "217 páginas",
      asin: "ASIN Amazon Kindle", asin_value: "B0GTJ19BD7",
      publisher_pending: "Editorial: próximamente",
      date_pending: "Fecha exacta de publicación: próximamente",
      en_title_label: "Título",
      en_title: "M.I.F.T INTEGRATED MEDIATION METHODOLOGY FOR TRANSNATIONAL FAMILIES: An operational guide for legal practice and mediation in transnational family disputes",
      en_authors: "Isabella Calamia Rinaldi y Diego Rycbczak Lopes",
      en_edition_value: "Primera edición (Kindle)",
      en_language_label: "Idioma", en_language_value: "Inglés",
      en_pubdate_label: "Fecha de publicación", en_pubdate_value: "25 de septiembre de 2026",
      en_formats_value: "Edición Kindle (e-book)",
      en_length_value: "225 páginas",
      en_asin_value: "B0HKZZPP1Z",
      overview: "Descripción general",
      overview_body: "La publicación MIFT está diseñada como un marco operativo para disputas familiares transnacionales e incluye herramientas profesionales estructuradas y recursos orientados a la implementación.",
      purchase_amazon_br: "Amazon Brasil — Kindle (edición en portugués)",
      purchase_amazon_us: "Amazon EE. UU. — Kindle (edición en inglés)",
    },
    about: {
      title: "Sobre MIFT", sub: "Una metodología operativa transfronteriza centrada en la niñez.",
      body1: "MIFT es una metodología operativa profesional desarrollada para apoyar a profesionales que trabajan con disputas familiares transnacionales. Es deliberadamente educativa y no adversarial — una arquitectura de apoyo a la decisión, no un producto de servicios legales.",
      body2: "MIFT no determina derechos legales, no proporciona asesoría legal y no sustituye la consulta con abogados calificados en cada jurisdicción relevante.",
      principles_title: "Principios centrales",
      pr1: "Centrada en la niñez", pr2: "Transfronteriza por diseño", pr3: "Educativa, no adversarial", pr4: "Consciente de la privacidad",
    },
    developer: {
      title: "Sobre la Desarrolladora", name: "Isabella Calamia Rinaldi",
      role: "Abogada brasileña enfocada en Derecho Internacional de Familia, mediación familiar internacional y resolución de disputas familiares transfronterizas. Desarrolladora de la metodología MIFT.",
      credentials_title: "Trayectoria profesional",
      c1: "Abogada brasileña; práctica en Derecho de Familia desde 2017.",
      c2: "Enfoque profesional en Derecho Internacional de Familia y disputas familiares transnacionales.",
      c3: "Posgrado en Derecho Internacional.",
      c4: "Posgrado en Derecho de Familia.",
      c5: "Candidata a LL.M. en la University of Dayton.",
      c6: "Experiencia en asuntos de familia internacionales y transfronterizos.",
      c7: "Autora/coautora y desarrolladora de la metodología MIFT.",
      c8: "Publicaciones profesionales, formaciones, presentaciones e investigación en el área (detalles verificados a añadir).",
      licensure_notice: "Autorizada para ejercer el derecho en Brasil. No admitida para ejercer el derecho en los Estados Unidos.",
      photo_placeholder: "Fotografía profesional próximamente",
    },
    research: {
      title: "Investigación y Recursos Institucionales",
      sub: "Una biblioteca curada de fuentes institucionales públicas relevantes para la resolución de disputas familiares transnacionales.",
      visit: "Visitar recurso",
      references_not_endorsements: "Estas son referencias educativas de instituciones independientes. Su inclusión no constituye un endoso de MIFT por parte de esos organismos, y MIFT no representa a ninguno de ellos ni tiene afiliación con ellos.",
      categories: { institutions: "Instituciones Internacionales y Autoridades Centrales", academic: "Académico e Investigación", professional: "Asociaciones Profesionales y Organismos de Mediación" },
    },
    disclaimer: {
      title: "Aviso Profesional",
      body: "La plataforma MIFT es un marco educativo profesional y una arquitectura de apoyo a la decisión. No es un producto de servicios legales. No determina derechos legales, no constituye asesoría legal, no crea una relación abogado-cliente y no sustituye la consulta con abogados calificados en cada jurisdicción relevante. La plataforma no acepta información específica del caso e intencionalmente no recoge nombres de menores, fechas de nacimiento, domicilios, números de expediente, información de pasaporte, situación migratoria individual, alegaciones detalladas de abuso, documentos judiciales u otros datos identificables. Sin honorarios, sin promesas de resultado, sin marketing de servicios legales, sin chatbot legal automatizado.",
      pre_tool: "MIFT es un marco educativo y profesional de apoyo a la decisión. No proporciona asesoría legal, no crea una relación abogado-cliente, no determina la jurisdicción ni sustituye el juicio de profesionales debidamente autorizados.",
      hague_urgency: "MIFT no busca demorar acciones judiciales, administrativas o de Autoridad Central urgentes. Los usuarios deben consultar prontamente a un abogado calificado sobre los procedimientos y plazos aplicables del Convenio de La Haya.",
    },
    forms: {
      full_name: "Nombre completo", email: "Correo electrónico", professional_field: "Área profesional", country: "País",
      organization: "Organización (opcional)", years_experience: "Años de experiencia",
      interested_pilot: "Interés en el programa piloto",
      interested_training: "Interés en formación",
      interested_collab: "Interés en colaboración",
      consent: "Entiendo que este es un marco educativo profesional, no asesoría legal.",
      required: "Obligatorio",
      caseload_size: "Volumen aproximado de casos transfronterizos",
      reason_to_join: "¿Por qué le gustaría participar en el piloto MIFT?",
      tool_used: "¿Qué herramienta MIFT utilizó?",
      usefulness: "Utilidad (1–5)", clarity: "Claridad (1–5)", would_recommend: "¿La recomendaría a un colega?",
      comments: "Comentarios (solo no identificables)",
      training_type: "Tipo de formación solicitada",
      collaboration_type: "Tipo de colaboración",
      description: "Descripción (no incluya detalles identificables del caso)",
    },
    footer: {
      copyright: "© MIFT — Metodología Integrada de Mediación para Familias Transnacionales",
      pre_launch: "Prelanzamiento / build MVP. Fecha de lanzamiento público por confirmar.",
      dev_log: "Registro de Desarrollo (admin)",
      full_disclaimer: "MIFT es un marco educativo y profesional de apoyo a la decisión. No proporciona asesoría legal, no crea una relación abogado-cliente, no determina la jurisdicción ni sustituye el juicio de profesionales debidamente autorizados. Las referencias institucionales listadas en la página Investigación son fuentes educativas, no endosos.",
    },
    admin: {
      title: "MIFT Admin",
      login_title: "Acceso administrativo",
      email: "Correo", password: "Contraseña", sign_in: "Entrar", sign_out: "Salir",
      unauthorized: "Credenciales inválidas",
      env_label: "Entorno", env_production: "Producción", env_test: "Prueba", env_all: "Todo",
      start_date: "Fecha inicial", end_date: "Fecha final", apply: "Aplicar", clear: "Limpiar",
      export_csv: "Exportar CSV",
      totals: "Totales agregados",
      by_country: "Por país", by_field: "Por área", by_language: "Por idioma",
      dev_log_title: "Registro de Desarrollo (solo hitos reales)",
      dev_log_add: "Añadir hito",
      dev_log_title_field: "Título", dev_log_version: "Versión (opcional)",
      dev_log_notes: "Notas", dev_log_occurred: "Fecha real de ocurrencia (ISO)",
      dev_log_save: "Guardar hito",
      no_data: "Aún no se ha registrado actividad de producción.",
    },
  },
};
